import React from 'react';

export const CaseStudies: React.FC = () => {
  const caseStudies = [
    {
      title: 'Suburban Atlanta Garden Community',
      location: 'Marietta, Georgia',
      units: '84 Units',
      situation: 'The owner had operated this 1980s-era garden apartment community for 15 years but was facing significant capital expenditure needs. The property needed new roofs on all buildings, parking lot resurfacing, HVAC replacements in 40+ units, and extensive landscaping improvements. Estimated repair costs exceeded $800,000.',
      challenge: 'The owner didn\'t have capital for major improvements and didn\'t want to take on debt. Traditional buyers were offering low prices due to the deferred maintenance, and several deals had fallen through due to inspection issues.',
      solution: 'We purchased the property as-is for a fair price that reflected the needed improvements. The owner avoided $800,000+ in repair costs and closed in 35 days without any contingencies or renegotiations.',
      outcome: 'The seller received cash at closing, avoided the stress of managing major renovations, and moved on to retirement without the burden of property management.'
    },
    {
      title: 'Multi-Family Portfolio Sale',
      location: 'Charlotte Metro Area',
      units: '3 Properties, 186 Total Units',
      situation: 'An investor owned three garden apartment properties across the Charlotte metro area. After 20 years of ownership, he wanted to exit the multifamily business entirely and simplify his life. The properties were in varying conditions, with one well-maintained, one needing moderate updates, and one requiring substantial renovations.',
      challenge: 'Selling three properties simultaneously to different buyers would be complex and time-consuming. The owner wanted a single transaction to avoid managing multiple closings and dealing with various buyers.',
      solution: 'We structured a portfolio purchase acquiring all three properties in one transaction. We provided a blended price that reflected each property\'s condition and market position, and closed all three simultaneously.',
      outcome: 'The seller completed his exit from multifamily real estate in one transaction, received cash for all three properties, and avoided the complexity of multiple sales processes.'
    },
    {
      title: 'Inherited Garden Apartments',
      location: 'Nashville Suburbs',
      units: '52 Units',
      situation: 'Three siblings inherited a garden apartment property from their parents. None of them had property management experience or interest in being landlords. The property was older, needed updates, and had some tenant issues. The siblings lived in different states and wanted to sell quickly to divide the proceeds.',
      challenge: 'The siblings needed to coordinate decisions across three parties, the property needed work to maximize value, and they wanted to avoid the time and expense of renovations. Family dynamics made decision-making challenging.',
      solution: 'We worked with all three siblings and their attorney to structure a straightforward purchase. We bought the property as-is, handled all tenant issues, and closed in 40 days with minimal involvement required from the sellers.',
      outcome: 'The siblings received fair value for the inherited property, avoided family conflicts over renovation decisions, and quickly divided the proceeds to move forward with their lives.'
    },
    {
      title: 'Retirement Exit Strategy',
      location: 'Phoenix, Arizona',
      units: '124 Units',
      situation: 'A couple in their late 60s had owned and managed this garden apartment community for 25 years. They were ready to retire but concerned about capital gains taxes from an outright sale. The property was well-maintained but showing its age, and they didn\'t want to invest in major renovations before retiring.',
      challenge: 'The sellers wanted to minimize tax impact while achieving fair value. They also needed flexibility on timing to coordinate with their retirement plans and relocation to another state.',
      solution: 'We structured a seller financing arrangement with a substantial down payment and monthly payments over 10 years. This installment sale treatment spread their capital gains over time, reducing annual tax liability while providing ongoing retirement income.',
      outcome: 'The sellers achieved their retirement goals with tax-efficient structure, received immediate cash from the down payment, and now receive monthly income that supplements their retirement. They exited property management immediately while optimizing their financial outcome.'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Real Garden Apartment Transactions
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            See how we've helped garden apartment owners successfully sell their properties and achieve their goals.
          </p>
        </div>

        <div className="mb-16">
          <div className="relative overflow-hidden rounded-2xl shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/54bc1a9a7bdb9387ab82bfff3bbd2ef0.jpg"
              alt="Successful Real Estate Transactions"
              className="w-full h-[450px] object-cover object-top"
            />
          </div>
        </div>

        <div className="space-y-12">
          {caseStudies.map((study, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-8 py-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {study.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-4 text-white/90">
                      <div className="flex items-center">
                        <i className="ri-map-pin-line mr-2"></i>
                        <span>{study.location}</span>
                      </div>
                      <div className="flex items-center">
                        <i className="ri-building-line mr-2"></i>
                        <span>{study.units}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8">
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-information-line text-orange-500 mr-2"></i>
                      Situation
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {study.situation}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-alert-line text-red-500 mr-2"></i>
                      Challenge
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {study.challenge}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-lightbulb-line text-blue-500 mr-2"></i>
                      Our Solution
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {study.solution}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-checkbox-circle-line text-green-500 mr-2"></i>
                      Outcome
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {study.outcome}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl border border-orange-200 p-10">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Your Success Story Starts Here
            </h3>
            <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
              Every garden apartment property and owner situation is unique. Let us create a customized solution for your specific needs and goals.
            </p>
            <a
              href="#get-offer"
              className="inline-block px-10 py-5 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl text-lg whitespace-nowrap"
            >
              Start Your Sale Today
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};